'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface QuoteCalculatorProps {
  className?: string;
}

const QuoteCalculator = ({ className = '' }: QuoteCalculatorProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [serviceType, setServiceType] = useState('residential');
  const [frequency, setFrequency] = useState('weekly');
  const [rooms, setRooms] = useState(3);

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const calculateEstimate = () => {
    const baseRates: Record<string, number> = {
      residential: 45,
      commercial: 65,
      specialized: 85,
    };

    const frequencyMultipliers: Record<string, number> = {
      weekly: 1.0,
      biweekly: 1.15,
      monthly: 1.3,
      oneTime: 1.5,
    };

    const baseRate = baseRates[serviceType] || 45;
    const frequencyMultiplier = frequencyMultipliers[frequency] || 1.0;
    const roomMultiplier = rooms;

    return Math.round(baseRate * frequencyMultiplier * roomMultiplier);
  };

  const content = {
    fr: {
      title: 'Calculateur de devis instantané',
      subtitle: 'Obtenez une estimation en CHF en quelques secondes',
      serviceLabel: 'Type de service',
      serviceOptions: {
        residential: 'Résidentiel',
        commercial: 'Commercial',
        specialized: 'Spécialisé',
      },
      frequencyLabel: 'Fréquence',
      frequencyOptions: {
        weekly: 'Hebdomadaire',
        biweekly: 'Bihebdomadaire',
        monthly: 'Mensuel',
        oneTime: 'Ponctuel',
      },
      roomsLabel: 'Nombre de pièces',
      estimateLabel: 'Estimation',
      perVisit: 'par visite',
      getQuote: 'Obtenir un devis détaillé',
      note: 'Prix indicatif. Devis personnalisé gratuit disponible.',
    },
    de: {
      title: 'Sofortiger Kostenrechner',
      subtitle: 'Erhalten Sie eine Schätzung in CHF in wenigen Sekunden',
      serviceLabel: 'Dienstleistungsart',
      serviceOptions: {
        residential: 'Wohnbereich',
        commercial: 'Gewerbe',
        specialized: 'Spezialisiert',
      },
      frequencyLabel: 'Häufigkeit',
      frequencyOptions: {
        weekly: 'Wöchentlich',
        biweekly: 'Zweiwöchentlich',
        monthly: 'Monatlich',
        oneTime: 'Einmalig',
      },
      roomsLabel: 'Anzahl der Räume',
      estimateLabel: 'Schätzung',
      perVisit: 'pro Besuch',
      getQuote: 'Detailliertes Angebot erhalten',
      note: 'Richtpreis. Kostenloses personalisiertes Angebot verfügbar.',
    },
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;
  const estimate = isHydrated ? calculateEstimate() : 135;

  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-br from-surface to-background ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-accent/20 px-4 py-2 rounded-full mb-4">
              <Icon name="CalculatorIcon" size={20} variant="solid" className="text-accent" />
              <span className="text-sm font-medium text-accent-foreground">Devis instantané</span>
            </div>
            <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              {currentContent.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {currentContent.subtitle}
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-card border border-border rounded-2xl shadow-xl p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Service Type */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                  {currentContent.serviceLabel}
                </label>
                <div className="space-y-2">
                  {Object.entries(currentContent.serviceOptions).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setServiceType(key)}
                      className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-300 ${
                        serviceType === key
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          serviceType === key ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {serviceType === key && (
                          <div className="w-3 h-3 rounded-full bg-primary"></div>
                        )}
                      </div>
                      <span className="font-medium text-foreground">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                  {currentContent.frequencyLabel}
                </label>
                <div className="space-y-2">
                  {Object.entries(currentContent.frequencyOptions).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setFrequency(key)}
                      className={`w-full flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-300 ${
                        frequency === key
                          ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          frequency === key ? 'border-primary' : 'border-border'
                        }`}
                      >
                        {frequency === key && (
                          <div className="w-3 h-3 rounded-full bg-primary"></div>
                        )}
                      </div>
                      <span className="font-medium text-foreground">{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Rooms Slider */}
            <div className="mb-8">
              <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                {currentContent.roomsLabel}: {rooms}
              </label>
              <input
                type="range"
                min="1"
                max="10"
                value={rooms}
                onChange={(e) => setRooms(Number(e.target.value))}
                className="w-full h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>1</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>

            {/* Estimate Display */}
            <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-8 mb-6">
              <div className="text-center">
                <div className="text-sm font-medium text-primary-foreground/80 mb-2">
                  {currentContent.estimateLabel}
                </div>
                <div className="text-5xl lg:text-6xl font-heading font-bold text-primary-foreground mb-2">
                  CHF {estimate}
                </div>
                <div className="text-sm text-primary-foreground/80">
                  {currentContent.perVisit}
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <Link
              href="/quote-request"
              className="w-full flex items-center justify-center gap-2 bg-accent hover:bg-accent/90 text-accent-foreground px-8 py-4 rounded-lg font-heading font-semibold text-lg transition-all duration-300 shadow-md hover:shadow-lg"
            >
              {currentContent.getQuote}
              <Icon name="ArrowRightIcon" size={20} variant="solid" />
            </Link>

            {/* Note */}
            <p className="text-xs text-muted-foreground text-center mt-4">
              {currentContent.note}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default QuoteCalculator;